import { jsx as _jsx } from "react/jsx-runtime";
import Hero from '../components/Hero/Hero';
import CtaBlock from '../components/CtaBlock/CtaBlock';
import Features from '../components/Features/Features';
import LandingHero from '../components/LandingHero/LandingHero';
import DepartmentPermitsPage from '../components/CMS/DepartmentPermitsPage/DepartmentPermitsPage';
import PermitDetailsViewPage from '../components/CMS/PermitDetailsViewPage/PermitDetailsViewPage';
import AgencyPlaceholderPage from '../components/CMS/AgencyPlaceholderPage/AgencyPlaceholderPage';
import AgencyContactsPage from '../components/CMS/AgencyContactsPage/AgencyContactsPage';
import GetStartedSection from '../components/CMS/GetStartedSection/GetStartedSection';
import HowItWorksSection from '../components/CMS/HowItWorksSection/HowItWorksSection';
import WhyChooseSection from '../components/CMS/WhyChooseSection/WhyChooseSection';
import { AccordionBlock, AlertBanner, ContactCard, TextBlock, ImageBlock, DividerBlock, InfoCard, LinkList, ProcessSteps, MapEmbed, VideoEmbed, Breadcrumb, SearchBlock, CategoryCards, AgencyList, PermitCard, PermitCardList, DataTable, ExpandableBlock, ContactSection, NavBar, StatsCounter, QuickActions, AnnouncementCard, Footer, } from '../components/CMS';
import TermsOfService from '../components/library/CMS/TermsOfService';
/** Library `TermsOfService` with this app's NavBar in its header band (page JSON passes `nav`). */
const TermsOfServicePage = ({ nav, ...props }) => (_jsx(TermsOfService, { ...props, header: nav ? _jsx(NavBar, { ...nav, embedInHero: true }) : undefined }));
/**
 * CMS component registry for JSON-driven pages (`PageRenderer`).
 * Each entry maps a JSON `component` string → React component; `defaultProps` document typical props (PageRenderer uses props from page JSON, not these defaults).
 * When adding a component: append to `cmsComponentsList` and extend `ComponentName`.
 */
export const cmsComponentsList = [
    {
        name: 'Hero',
        Component: Hero,
        defaultProps: {
            label: '',
            title: 'Welcome to West Virginia OneStop',
            subtitle: 'The central hub for West Virginia state licenses, forms, permits, and registrations.',
            primaryCta: { label: 'Log in', href: '/login' },
            secondaryCta: { label: 'Register', href: '/register' },
            gradient: { from: '#002855', to: '#1e3d6b', direction: '135deg' },
        },
    },
    {
        name: 'LandingHero',
        Component: LandingHero,
        defaultProps: {
            title: 'West Virginia OneStop Permit Portal',
            subheading: 'Permits & Licenses All In One Place',
            description: "Learn how to start a new business, open a new business permit, find resources for small businesses, and more on West Virginia's portal.",
            nav: {
                logoText: 'West Virginia',
                links: [
                    { label: 'Home', href: '#hero' },
                    { label: 'Categories', href: '#categories' },
                    { label: 'Agencies', href: '#agencies' },
                    { label: 'Departments', href: '#departments' },
                    { label: 'How It Works', href: '#how-it-works' },
                ],
                loginLabel: 'Login',
                loginHref: '/login',
                ctaLabel: 'Sign Up',
                ctaHref: '/register',
            },
            searchPlaceholder: 'Search Permits, Licenses, or Services',
            searchButtonLabel: 'Search',
            departmentsHeading: 'Departments',
            departmentsSubheading: 'Choose a department to browse permits and services.',
            categoriesHeading: 'Categories',
            categories: [],
            departments: [],
            features: [],
            backgroundImage: '/images/WestVirginia/2026.03.27_One-Stop Shop Website/West_Virginia_State_Capitol_-_Charleston__1_.webp',
            heroBackgroundPosition: 'center 5%',
            heroBackgroundSize: '140% auto',
        },
    },
    {
        name: 'GetStartedSection',
        Component: GetStartedSection,
        defaultProps: {
            heading: 'Get started',
            subheading: 'Popular paths through the portal.',
            items: [
                {
                    icon: 'Assignment',
                    title: 'Apply for a Permit',
                    description: 'Start a new application online.',
                    href: '/apply',
                    accent: 'gold',
                },
            ],
            overlapSpacing: 5,
        },
    },
    {
        name: 'HowItWorksSection',
        Component: HowItWorksSection,
        defaultProps: {
            heading: 'How it works',
            steps: [
                { icon: 'Search', title: 'Find', description: 'Search or browse for your permit.', },
                { icon: 'Assignment', title: 'Apply', description: 'Complete your application online.', },
            ],
        },
    },
    {
        name: 'WhyChooseSection',
        Component: WhyChooseSection,
        defaultProps: {
            heading: 'Why OneStop',
            subheading: 'One portal for state services.',
            items: [
                { icon: 'VerifiedUser', title: 'Secure', description: 'Official state services.', },
            ],
            image: '/images/WestVirginia/2026.03.27_One-Stop Shop Website/West_Virginia_State_Capitol_-_Charleston__1_.webp',
            imageAlt: 'West Virginia State Capitol',
        },
    },
    {
        name: 'CtaBlock',
        Component: CtaBlock,
        defaultProps: {
            label: '',
            heading: 'Call to action',
            description: 'Description here.',
            buttonLabel: 'Button',
            buttonHref: '#',
        },
    },
    {
        name: 'Features',
        Component: Features,
        defaultProps: {
            label: '',
            heading: 'Features',
            items: [
                { title: 'Feature 1', description: 'Description one.', icon: '◆' },
                { title: 'Feature 2', description: 'Description two.', icon: '◇' },
                { title: 'Feature 3', description: 'Description three.', icon: '○' },
            ],
        },
    },
    {
        name: 'AccordionBlock',
        Component: AccordionBlock,
        defaultProps: {
            label: 'FAQ',
            items: [
                { question: 'What is this?', answer: 'This is the first FAQ answer. You can add more items via props or flow.json.' },
                { question: 'How do I use it?', answer: 'Use the AccordionBlock component with an items array of { question, answer }. Supports label and gradient like other blocks.' },
                { question: 'Can multiple panels stay open?', answer: 'Set allowMultiple: true in props to allow multiple accordion panels open at once.' },
            ],
            allowMultiple: false,
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'AlertBanner',
        Component: AlertBanner,
        defaultProps: {
            label: 'Notice',
            severity: 'info',
            title: 'Important update',
            message: 'This is an alert banner. Set severity to info, success, warning, or error. Use dismissible: true to show a close button.',
            dismissible: true,
            gradient: { from: '#e3f2fd', to: '#bbdefb', direction: '180deg' },
        },
    },
    {
        name: 'ContactCard',
        Component: ContactCard,
        defaultProps: {
            label: 'Contact',
            name: 'Support',
            phone: '+1 (555) 123-4567',
            email: 'support@example.com',
            address: '123 Main St, City, ST 12345',
            website: 'https://example.com',
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'TextBlock',
        Component: TextBlock,
        defaultProps: {
            label: 'Content',
            content: '<p>Rich text content here. Edit via flow.json or editor.</p>',
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'ImageBlock',
        Component: ImageBlock,
        defaultProps: {
            label: 'Image',
            src: 'https://via.placeholder.com/800x400?text=Image',
            alt: 'Placeholder',
            caption: 'Optional caption',
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'DividerBlock',
        Component: DividerBlock,
        defaultProps: {
            label: '',
            style: 'solid',
            thickness: 1,
            spacing: 2,
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'InfoCard',
        Component: InfoCard,
        defaultProps: {
            label: 'Highlight',
            title: 'Info card title',
            description: 'Short description or feature highlight. Supports icon and gradient.',
            icon: 'Info',
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'LinkList',
        Component: LinkList,
        defaultProps: {
            label: 'Links',
            title: 'Quick links',
            links: [
                { text: 'First link', url: '#', icon: 'ArrowForward' },
                { text: 'Second link', url: '#', icon: 'Link' },
            ],
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'ProcessSteps',
        Component: ProcessSteps,
        defaultProps: {
            label: 'How it works',
            steps: [
                { title: 'Step 1', description: 'First step description.', icon: 'LooksOne' },
                { title: 'Step 2', description: 'Second step description.', icon: 'LooksTwo' },
                { title: 'Step 3', description: 'Third step description.', icon: 'Looks3' },
            ],
            layout: 'vertical',
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'MapEmbed',
        Component: MapEmbed,
        defaultProps: {
            label: 'Location',
            title: 'Find us',
            address: '1600 Amphitheatre Parkway, Mountain View, CA',
            height: '220',
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'VideoEmbed',
        Component: VideoEmbed,
        defaultProps: {
            label: 'Video',
            title: 'Watch this',
            url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
            aspectRatio: '16:9',
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'Breadcrumb',
        Component: Breadcrumb,
        defaultProps: {
            label: '',
            items: [
                { label: 'OneStop', href: '/' },
                { label: 'Departments', href: '/departments' },
                { label: 'Secretary of State' },
            ],
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'SearchBlock',
        Component: SearchBlock,
        defaultProps: {
            label: '',
            placeholder: 'Search licenses and permits…',
            buttonLabel: 'Search',
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'CategoryCards',
        Component: CategoryCards,
        defaultProps: {
            label: '',
            heading: 'Browse by category',
            items: [
                { title: 'Professional', description: 'Licenses and permits for individuals.', linkText: 'Browse', linkHref: '/professional' },
                { title: 'Organizational', description: 'Licenses and permits for business use.', linkText: 'Browse', linkHref: '/organizational' },
                { title: 'Personal', description: 'Licenses and permits for personal use.', linkText: 'Browse', linkHref: '/personal' },
            ],
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'MenuItems',
        Component: AgencyList,
        defaultProps: {
            label: '',
            heading: 'Browse by State Agencies',
            countLabel: 'applications available',
            linkText: 'Browse',
            emptyMessage: 'No agencies to display.',
            items: [
                { name: 'Secretary of State', count: 4, href: '/permits' },
                { name: 'Department of Agriculture', count: 107, href: '#' },
            ],
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'PermitCard',
        Component: PermitCard,
        defaultProps: {
            label: '',
            title: 'Notary Commission',
            description: 'In Maryland, an individual who wishes to be commissioned as a notary public may obtain an application from Office of the Secretary of State.',
            distributedBy: 'Secretary of State',
            timeframe: '2-14 days',
            price: '$0.00',
            detailsHref: '/licenses/notary-commission',
            linkText: 'View Details',
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'ResultItems',
        Component: PermitCardList,
        defaultProps: {
            label: '',
            heading: 'Results',
            items: [
                { title: 'Notary Commission', description: 'Commission as a notary public.', distributedBy: 'Secretary of State', timeframe: '2-14 days', price: '$0.00', detailsHref: '/licenses/notary-commission' },
                { title: 'SOS Charities Initial Registration Form', description: 'Online registration for charitable organizations.', distributedBy: 'Office of the Secretary of State', timeframe: '15-45 day', price: '$0.00 - $300.00', detailsHref: '#' },
            ],
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'DataTable',
        Component: DataTable,
        defaultProps: {
            label: '',
            caption: 'Application Determination Timeframe',
            headers: ['Complete Application', 'Application Determination', ''],
            rows: [
                ['New notary application', '15 minutes', '2-14 days'],
                ['Renewal notary application', '10 minutes', '1-3 days'],
                ['Remote notary notification form', '5 minutes', '1-3 days'],
            ],
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'ExpandableBlock',
        Component: ExpandableBlock,
        defaultProps: {
            label: '',
            title: 'Legal Authority',
            content: '<p>Annotated Code of Maryland, State Government Article, Title 18, Notaries Public.</p>',
            defaultExpanded: false,
            showMoreLabel: 'Show more',
            showLessLabel: 'Show less',
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'ContactSection',
        Component: ContactSection,
        defaultProps: {
            label: '',
            heading: 'Agency/Contact',
            issuerName: 'Secretary of State',
            issuerUrl: 'https://sos.maryland.gov',
            phone: '(410) 974-5521',
            hours: '8:30AM - 4:30PM',
            address: '16 Francis St., Annapolis, MD, 21401',
            gradient: { from: '#f8fafc', to: '#f1f5f9', direction: '180deg' },
        },
    },
    {
        name: 'NavBar',
        Component: NavBar,
        defaultProps: {
            logoText: 'OneStop',
            links: [
                { label: 'Home', href: '/' },
                { label: 'Services', href: '/services' },
                { label: 'Agencies', href: '/agencies' },
                { label: 'Help', href: '/help' },
            ],
            ctaLabel: 'Sign In',
            ctaHref: '/login',
        },
    },
    {
        name: 'StatsCounter',
        Component: StatsCounter,
        defaultProps: {
            heading: 'Portal at a Glance',
            items: [
                { value: '10,000+', label: 'Permits Processed', icon: 'Description' },
                { value: '50+', label: 'State Agencies', icon: 'AccountBalance' },
                { value: '125+', label: 'Services Available', icon: 'CheckCircle' },
                { value: '24/7', label: 'Online Access', icon: 'AccessTime' },
            ],
            gradient: { from: '#002855', to: '#1e3d6b', direction: '135deg' },
        },
    },
    {
        name: 'QuickActions',
        Component: QuickActions,
        defaultProps: {
            heading: 'Popular Services',
            subheading: 'Quick access to frequently used services.',
            items: [
                { title: 'Apply for a Permit', icon: 'Assignment', href: '/apply', description: 'Start a new application.' },
                { title: 'Check Status', icon: 'Search', href: '/status', description: 'Track your application.' },
                { title: 'Renew License', icon: 'Refresh', href: '/renew', description: 'Renew an existing license.' },
                { title: 'Pay Fees', icon: 'Payment', href: '/pay', description: 'Make a payment online.' },
            ],
            gradient: { from: '#ffffff', to: '#f0f4f8', direction: '180deg' },
        },
    },
    {
        name: 'AnnouncementCard',
        Component: AnnouncementCard,
        defaultProps: {
            heading: 'Latest Updates',
            items: [
                { title: 'New Online Services', date: 'Mar 15, 2026', summary: 'Additional permit types now available online.', href: '#', category: 'Update' },
                { title: 'System Maintenance', date: 'Mar 10, 2026', summary: 'Scheduled maintenance this weekend.', href: '#', category: 'Notice' },
            ],
            gradient: { from: '#f8fafc', to: '#f0f4f8', direction: '180deg' },
        },
    },
    {
        name: 'Footer',
        Component: Footer,
        defaultProps: {
            logoText: 'West Virginia',
            description: 'The official permit and licensing portal for West Virginia.',
            phone: '(304) 558-3456',
            email: 'onestop@wv.gov',
            address: '1900 Kanawha Blvd E, Charleston, WV 25305',
            linkGroups: [
                { title: 'Services', links: [{ label: 'Apply', href: '/apply' }, { label: 'Renew', href: '/renew' }] },
                { title: 'Help', links: [{ label: 'FAQ', href: '/faq' }, { label: 'Contact', href: '/contact' }] },
            ],
        },
    },
    {
        name: 'DepartmentPermitsPage',
        Component: DepartmentPermitsPage,
        defaultProps: {
            nav: {
                logoText: 'WEST VIRGINIA',
                links: [
                    { label: 'Home', href: '/' },
                    { label: 'Departments', href: '/#departments' },
                ],
                loginLabel: 'Login',
                loginHref: '/login',
                ctaLabel: 'Sign Up',
                ctaHref: '/register',
            },
            departments: {},
            agenciesHeading: 'Agencies',
            agenciesSubheading: 'Expand an agency to browse permits, fees, timelines, and application details.',
            searchPlaceholder: 'Search ...',
            searchButtonLabel: 'Search',
        },
    },
    {
        name: 'PermitDetailsViewPage',
        Component: PermitDetailsViewPage,
        defaultProps: {
            nav: {
                logoText: 'WEST VIRGINIA',
                links: [
                    { label: 'Home', href: '/' },
                    { label: 'Departments', href: '/#departments' },
                ],
                loginLabel: 'Login',
                loginHref: '/login',
                ctaLabel: 'Sign Up',
                ctaHref: '/register',
            },
        },
    },
    {
        name: 'AgencyPlaceholderPage',
        Component: AgencyPlaceholderPage,
        defaultProps: {
            intro: 'Permits, licenses, and services for this agency will be listed here. This page is under construction.',
            nav: {
                logoText: 'WEST VIRGINIA',
                links: [
                    { label: 'Home', href: '/' },
                    { label: 'Agencies', href: '/#agencies' },
                    { label: 'Departments', href: '/#departments' },
                ],
                loginLabel: 'Login',
                loginHref: '/login',
                ctaLabel: 'Sign Up',
                ctaHref: '/register',
            },
        },
    },
    {
        name: 'AgencyContactsPage',
        Component: AgencyContactsPage,
        defaultProps: {
            heading: 'Agency Contacts',
        },
    },
    {
        name: 'TermsOfService',
        Component: TermsOfServicePage,
        defaultProps: {
            title: 'Terms of Service',
            sections: [],
        },
    },
];
export const cmsComponentNames = cmsComponentsList.map((c) => c.name);
/** Maps JSON `section.component` name → React component. Consumed by `PageRenderer`. */
export const componentRegistry = Object.fromEntries(cmsComponentsList.map((def) => [def.name, def.Component]));
